import {
	Activity,
	Clock,
	Eye,
	FileText,
	LogOut,
	MousePointerClick,
	Radio,
	Search,
	ShieldCheck,
	TrendingUp,
	Users,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { SERVER_URL } from "../api/client";
import { fetchJson } from "../api/rest";
import { PageHeader } from "../components/layout/PageHeader";

interface Overview {
	activeSessions: number;
	searchesToday: number;
	searchingSessionsToday: number;
	zeroResultToday: number;
	clicksToday: number;
	clickThroughRate: number;
}
interface TimePoint {
	day: string;
	searches: number;
	clicks: number;
}
interface QueryRow {
	query: string;
	searches: number;
	avgResults?: number;
}
interface ProductRow {
	productId: number;
	productName: string | null;
	productUrl: string | null;
	clicks: number;
	avgPosition: number;
}
interface PositionRow {
	position: number;
	clicks: number;
}
interface HourPoint {
	hour: string;
	label: string;
	searches: number;
	zeroResults: number;
	clicks: number;
}
interface RecentSearchRow {
	id: number;
	query: string;
	resultCount: number | null;
	sessionId: string | null;
	createdAt: string;
	clicks: number;
}
interface SiteOverview {
	liveVisits: number;
	liveWindowMinutes: number;
	visitsToday: number;
	pageviewsToday: number;
	pagesPerVisit: number | null;
	bounceRate: number | null;
	avgVisitSeconds: number;
	consentRate: number | null;
	unansweredConsent: number;
}
interface SitePoint {
	day: string;
	visits: number;
	pageviews: number;
	consented: number;
}
interface LivePage {
	path: string;
	visits: number;
}
interface LandingRow {
	path: string;
	visits: number;
	bounceRate: number;
	pagesPerVisit: number;
	avgSeconds: number;
}
interface ReferrerRow {
	host: string | null;
	visits: number;
}
interface VisitRow {
	id: number;
	startedAt: string;
	landingPath: string;
	referrerHost: string | null;
	pageviews: number;
	statisticsConsent: boolean | null;
	seconds: number;
	live: boolean;
	path: { path: string; seconds: number }[];
}
interface LiveEvent {
	eventType: string;
	sessionId: string | null;
	payload?: {
		query?: string;
		productId?: string | number;
		objectPosition?: number;
	};
}

// Derive the WebSocket URL from the (absolute) API URL. /ws/live is a route on
// the API server, so it keeps whatever prefix reaches that server — deployed,
// nginx only forwards /api to it and serves everything else from the SPA
// container, so stripping /api pointed the socket at the static site instead.
const resolveWsUrl = (): string => {
	try {
		const u = new URL(SERVER_URL);
		u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
		u.pathname = `${u.pathname.replace(/\/$/, "")}/ws/live`;
		u.search = "";
		return u.toString();
	} catch {
		return "";
	}
};

const StatCard = ({
	icon: Icon,
	label,
	value,
}: {
	icon: typeof Users;
	label: string;
	value: string | number;
}) => (
	<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
		<p className="flex items-center gap-2 font-medium text-[0.7rem] text-zinc-500 uppercase tracking-[0.14em]">
			<Icon className="h-3.5 w-3.5 text-zinc-400" />
			{label}
		</p>
		<p className="tnum mt-3 font-semibold text-[26px] text-ink leading-none">
			{value}
		</p>
	</div>
);

const Panel = ({
	title,
	actions,
	children,
}: {
	title: string;
	actions?: React.ReactNode;
	children: React.ReactNode;
}) => (
	<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
		<div className="mb-4 flex items-center justify-between gap-3">
			<h3 className="text-sm font-semibold text-slate-700">{title}</h3>
			{actions}
		</div>
		{children}
	</div>
);

const Switch = <T extends string>({
	options,
	value,
	onChange,
}: {
	options: readonly { id: T; label: string }[];
	value: T;
	onChange: (id: T) => void;
}) => (
	<div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
		{options.map((o) => (
			<button
				key={o.id}
				type="button"
				onClick={() => onChange(o.id)}
				className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
					value === o.id
						? "bg-white text-slate-800 shadow-sm"
						: "text-slate-500 hover:text-slate-700"
				}`}
			>
				{o.label}
			</button>
		))}
	</div>
);

const RANGES = [
	{ label: "7d", days: 7 },
	{ label: "30d", days: 30 },
	{ label: "90d", days: 90 },
];

const RECENT_HOURS = 48;
const RECENT_VIEWS = [
	{ id: "recent", label: "Recent" },
	{ id: "hourly", label: "By hour" },
	{ id: "top", label: "Top queries" },
] as const;
type RecentView = (typeof RECENT_VIEWS)[number]["id"];

// "2026-09-10T14:03:22" → "09-10 14:03". The server already formats these in
// its own timezone, so they must not go through Date — that would re-interpret
// a naive timestamp as UTC and shift every row.
const shortStamp = (iso: string) => `${iso.slice(5, 10)} ${iso.slice(11, 16)}`;

const duration = (seconds: number) =>
	seconds < 60
		? `${seconds}s`
		: `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, "0")}s`;

const consentLabel = (value: boolean | null) =>
	value === null ? "—" : value ? "yes" : "no";

const SITE_REFRESH_MS = 30_000;

const SiteTraffic = ({ days }: { days: number }) => {
	const [overview, setOverview] = useState<SiteOverview | null>(null);
	const [series, setSeries] = useState<SitePoint[]>([]);
	const [live, setLive] = useState<LivePage[]>([]);
	const [landing, setLanding] = useState<LandingRow[]>([]);
	const [referrers, setReferrers] = useState<ReferrerRow[]>([]);
	const [visits, setVisits] = useState<VisitRow[]>([]);
	const [error, setError] = useState<string | null>(null);

	const loadLive = useCallback(async () => {
		const [ov, lp, rv] = await Promise.all([
			fetchJson<SiteOverview>("/analytics/site/overview"),
			fetchJson<LivePage[]>("/analytics/site/live-pages"),
			fetchJson<VisitRow[]>("/analytics/site/visits?limit=50"),
		]);
		setOverview(ov);
		setLive(lp);
		setVisits(rv);
	}, []);

	const loadRange = useCallback(async () => {
		const [ts, lr, rf] = await Promise.all([
			fetchJson<SitePoint[]>(`/analytics/site/over-time?days=${days}`),
			fetchJson<LandingRow[]>(
				`/analytics/site/landing-pages?days=${days}&limit=20`,
			),
			fetchJson<ReferrerRow[]>(
				`/analytics/site/referrers?days=${days}&limit=15`,
			),
		]);
		setSeries(ts);
		setLanding(lr);
		setReferrers(rf);
	}, [days]);

	useEffect(() => {
		const run = () => loadLive().catch((e: Error) => setError(e.message));
		run();
		const timer = setInterval(run, SITE_REFRESH_MS);
		return () => clearInterval(timer);
	}, [loadLive]);

	useEffect(() => {
		loadRange().catch((e: Error) => setError(e.message));
	}, [loadRange]);

	return (
		<div className="space-y-6">
			<div>
				<h2 className="text-lg font-semibold text-slate-800">Site traffic</h2>
				<p className="text-sm text-slate-500">
					Every visit, with or without cookie consent. Counted on the server
					with no cookies and no identifier kept: a visit is a browser seen
					again within 30 minutes on the same day.
				</p>
			</div>

			{error && (
				<div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
					{error}
				</div>
			)}

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
				<StatCard
					icon={Radio}
					label={`Live now (${overview?.liveWindowMinutes ?? 5}m)`}
					value={overview?.liveVisits ?? "—"}
				/>
				<StatCard
					icon={Users}
					label="Visits today"
					value={overview?.visitsToday ?? "—"}
				/>
				<StatCard
					icon={Eye}
					label="Page views today"
					value={overview?.pageviewsToday ?? "—"}
				/>
				<StatCard
					icon={FileText}
					label="Pages per visit"
					value={overview?.pagesPerVisit ?? "—"}
				/>
				<StatCard
					icon={LogOut}
					label="Single-page visits"
					value={overview?.bounceRate == null ? "—" : `${overview.bounceRate}%`}
				/>
				<StatCard
					icon={Clock}
					label="Avg. visit length"
					value={overview ? duration(overview.avgVisitSeconds) : "—"}
				/>
				<StatCard
					icon={ShieldCheck}
					label="Accepted statistics today"
					value={
						overview?.consentRate == null ? "—" : `${overview.consentRate}%`
					}
				/>
				<StatCard
					icon={Activity}
					label="No cookie choice yet"
					value={overview?.unansweredConsent ?? "—"}
				/>
			</div>

			<Panel title={`Visits & page views (last ${days} days)`}>
				<ResponsiveContainer width="100%" height={280}>
					<LineChart
						data={series}
						margin={{ top: 5, right: 20, bottom: 5, left: -10 }}
					>
						<CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
						<XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="#94a3b8" />
						<YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
						<Tooltip />
						<Line
							type="monotone"
							dataKey="visits"
							name="Visits"
							stroke="#6366f1"
							strokeWidth={2}
							dot={false}
						/>
						<Line
							type="monotone"
							dataKey="pageviews"
							name="Page views"
							stroke="#0ea5e9"
							strokeWidth={2}
							dot={false}
						/>
						<Line
							type="monotone"
							dataKey="consented"
							name="Visits that accepted statistics"
							stroke="#14b8a6"
							strokeWidth={2}
							strokeDasharray="4 3"
							dot={false}
						/>
					</LineChart>
				</ResponsiveContainer>
			</Panel>

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
				<div className="space-y-6">
					<Panel title="Live now — current page">
						<Table
							head={["Page", "Visits"]}
							rows={live.map((p) => [p.path, String(p.visits)])}
						/>
					</Panel>
					<Panel title="Arrived from">
						<Table
							head={["Site", "Visits"]}
							rows={referrers.map((r) => [
								r.host ?? "Direct / unknown",
								String(r.visits),
							])}
						/>
					</Panel>
				</div>
				<div className="lg:col-span-2">
					<Panel title="Landing pages">
						<Table
							head={[
								"Landing page",
								"Visits",
								"Single-page",
								"Pages/visit",
								"Avg. length",
							]}
							rows={landing.map((l) => [
								l.path,
								String(l.visits),
								`${l.bounceRate}%`,
								String(l.pagesPerVisit),
								duration(l.avgSeconds),
							])}
						/>
					</Panel>
				</div>
			</div>

			<Panel title="Latest visits">
				<div className="max-h-[32rem] overflow-y-auto">
					<Table
						head={[
							"Started",
							"Route through the site",
							"Pages",
							"Length",
							"Statistics",
							"From",
						]}
						rows={visits.map((v) => [
							`${v.live ? "● " : ""}${shortStamp(v.startedAt)}`,
							v.path
								.map((p) => `${p.path} (${duration(p.seconds)})`)
								.join(" → ") || v.landingPath,
							String(v.pageviews),
							duration(v.seconds),
							consentLabel(v.statisticsConsent),
							v.referrerHost ?? "—",
						])}
					/>
				</div>
			</Panel>
		</div>
	);
};

const Analytics = () => {
	const [days, setDays] = useState(30);
	const [overview, setOverview] = useState<Overview | null>(null);
	const [series, setSeries] = useState<TimePoint[]>([]);
	const [queries, setQueries] = useState<QueryRow[]>([]);
	const [products, setProducts] = useState<ProductRow[]>([]);
	const [positions, setPositions] = useState<PositionRow[]>([]);
	const [zeroResults, setZeroResults] = useState<QueryRow[]>([]);
	const [live, setLive] = useState<LiveEvent[]>([]);
	const [liveCount, setLiveCount] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const [liveStatus, setLiveStatus] = useState<
		"connecting" | "open" | "closed"
	>("connecting");
	const wsRef = useRef<WebSocket | null>(null);
	const [recentView, setRecentView] = useState<RecentView>("recent");
	const [hourly, setHourly] = useState<HourPoint[]>([]);
	const [recent, setRecent] = useState<RecentSearchRow[]>([]);
	const [recentTop, setRecentTop] = useState<QueryRow[]>([]);
	const [recentError, setRecentError] = useState<string | null>(null);

	const load = useCallback(async () => {
		const [ov, ts, tq, tp, bp, zr] = await Promise.all([
			fetchJson<Overview>("/analytics/overview"),
			fetchJson<TimePoint[]>(`/analytics/searches-over-time?days=${days}`),
			fetchJson<QueryRow[]>(`/analytics/top-queries?days=${days}&limit=20`),
			fetchJson<ProductRow[]>(`/analytics/top-products?days=${days}&limit=20`),
			fetchJson<PositionRow[]>(`/analytics/clicks-by-position?days=${days}`),
			fetchJson<QueryRow[]>(
				`/analytics/zero-result-searches?days=${days}&limit=20`,
			),
		]);
		setOverview(ov);
		setSeries(ts);
		setQueries(tq);
		setProducts(tp);
		setPositions(bp);
		setZeroResults(zr);
	}, [days]);

	// A failed load used to reach the console only, leaving the cards showing 0 —
	// indistinguishable from a genuinely quiet day.
	useEffect(() => {
		setError(null);
		load().catch((e: Error) => setError(e.message));
	}, [load]);

	// The 48-hour panel is independent of the day range above, and only the
	// selected view is fetched — switching costs one request, not three.
	const loadRecent = useCallback(async () => {
		if (recentView === "hourly") {
			setHourly(
				await fetchJson<HourPoint[]>(
					`/analytics/searches-by-hour?hours=${RECENT_HOURS}`,
				),
			);
		} else if (recentView === "top") {
			setRecentTop(
				await fetchJson<QueryRow[]>(
					`/analytics/top-queries-by-hour?hours=${RECENT_HOURS}&limit=25`,
				),
			);
		} else {
			setRecent(
				await fetchJson<RecentSearchRow[]>(
					`/analytics/recent-searches?hours=${RECENT_HOURS}&limit=100`,
				),
			);
		}
	}, [recentView]);

	useEffect(() => {
		setRecentError(null);
		const run = () =>
			loadRecent().catch((e: Error) => setRecentError(e.message));
		run();
		const timer = setInterval(run, 60_000);
		return () => clearInterval(timer);
	}, [loadRecent]);

	// Realtime stream — mirrors the DB via Redis pub/sub over a WebSocket.
	useEffect(() => {
		const url = resolveWsUrl();
		if (!url) return;
		const ws = new WebSocket(url);
		wsRef.current = ws;
		ws.onopen = () => setLiveStatus("open");
		ws.onclose = () => setLiveStatus("closed");
		ws.onmessage = (ev) => {
			try {
				const event = JSON.parse(ev.data) as LiveEvent;
				setLive((prev) => [event, ...prev].slice(0, 40));
				setLiveCount((c) => c + 1);
			} catch {
				// ignore malformed frames
			}
		};
		ws.onerror = () => setLiveStatus("closed");
		return () => ws.close();
	}, []);

	const maxPositionClicks = useMemo(
		() => Math.max(1, ...positions.map((p) => p.clicks)),
		[positions],
	);

	return (
		<div className="space-y-6">
			<PageHeader
				kicker="Menu · Analytics"
				title="Analytics."
				qualifier="Traffic, search and clicks."
				description={<p>Site traffic, search & click insights</p>}
				actions={
					<div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1">
						{RANGES.map((r) => (
							<button
								key={r.days}
								type="button"
								onClick={() => setDays(r.days)}
								className={`rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors ${
									days === r.days
										? "bg-primary text-white"
										: "text-slate-500 hover:bg-slate-100"
								}`}
							>
								{r.label}
							</button>
						))}
					</div>
				}
			/>

			{error && (
				<div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
					{error}
				</div>
			)}

			<SiteTraffic days={days} />

			<div className="border-t border-slate-200 pt-6">
				<h2 className="text-lg font-semibold text-slate-800">
					Search & clicks
				</h2>
				<p className="text-sm text-slate-500">
					Only visitors who accepted statistics.
				</p>
			</div>

			{/* Stat cards */}
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
				<StatCard
					icon={Users}
					label="Active sessions (30m)"
					value={overview?.activeSessions ?? "—"}
				/>
				<StatCard
					icon={Search}
					label="Searches today"
					value={overview?.searchesToday ?? "—"}
				/>
				<StatCard
					icon={MousePointerClick}
					label="Clicks today"
					value={overview?.clicksToday ?? "—"}
				/>
				<StatCard
					icon={TrendingUp}
					label="CTR today"
					value={overview ? `${overview.clickThroughRate}%` : "—"}
				/>
				<StatCard icon={Activity} label="Live events" value={liveCount} />
			</div>

			{/* Time series */}
			<Panel title={`Searches & clicks (last ${days} days)`}>
				<ResponsiveContainer width="100%" height={280}>
					<LineChart
						data={series}
						margin={{ top: 5, right: 20, bottom: 5, left: -10 }}
					>
						<CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
						<XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="#94a3b8" />
						<YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
						<Tooltip />
						<Line
							type="monotone"
							dataKey="searches"
							stroke="#6366f1"
							strokeWidth={2}
							dot={false}
						/>
						<Line
							type="monotone"
							dataKey="clicks"
							stroke="#10b981"
							strokeWidth={2}
							dot={false}
						/>
					</LineChart>
				</ResponsiveContainer>
			</Panel>

			{/* Last 48 hours — switchable between the raw feed, hourly volume and
			    the window's top queries */}
			<Panel
				title={`Searches — last ${RECENT_HOURS} hours`}
				actions={
					<Switch
						options={RECENT_VIEWS}
						value={recentView}
						onChange={setRecentView}
					/>
				}
			>
				{recentError && (
					<p className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
						{recentError}
					</p>
				)}

				{recentView === "hourly" && (
					<ResponsiveContainer width="100%" height={260}>
						<BarChart
							data={hourly}
							margin={{ top: 5, right: 20, bottom: 5, left: -10 }}
						>
							<CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
							<XAxis
								dataKey="hour"
								tickFormatter={(_v, i) => hourly[i]?.label ?? ""}
								interval={5}
								tick={{ fontSize: 11 }}
								stroke="#94a3b8"
							/>
							<YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
							<Tooltip />
							<Bar dataKey="searches" fill="#6366f1" radius={[4, 4, 0, 0]} />
							<Bar dataKey="clicks" fill="#10b981" radius={[4, 4, 0, 0]} />
						</BarChart>
					</ResponsiveContainer>
				)}

				{recentView === "top" && (
					<Table
						head={["Query", "Searches", "Avg results"]}
						rows={recentTop.map((q) => [
							q.query,
							String(q.searches),
							String(q.avgResults ?? "—"),
						])}
					/>
				)}

				{recentView === "recent" && (
					<div className="max-h-96 overflow-y-auto">
						<Table
							head={["Time", "Query", "Results", "Clicks"]}
							rows={recent.map((r) => [
								shortStamp(r.createdAt),
								r.query,
								r.resultCount === null ? "—" : String(r.resultCount),
								String(r.clicks),
							])}
						/>
					</div>
				)}
			</Panel>

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
				{/* Clicks by position */}
				<Panel title="Clicks by result position">
					<ResponsiveContainer width="100%" height={240}>
						<BarChart
							data={positions}
							margin={{ top: 5, right: 20, bottom: 5, left: -10 }}
						>
							<CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
							<XAxis
								dataKey="position"
								tick={{ fontSize: 11 }}
								stroke="#94a3b8"
							/>
							<YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
							<Tooltip />
							<Bar dataKey="clicks" fill="#6366f1" radius={[4, 4, 0, 0]} />
						</BarChart>
					</ResponsiveContainer>
					<p className="mt-2 text-xs text-slate-400">
						Peak {maxPositionClicks} clicks at a single position
					</p>
				</Panel>

				{/* Live feed */}
				<Panel title="Live event stream">
					<div className="max-h-60 space-y-1 overflow-y-auto text-[13px]">
						{live.length === 0 && (
							<p className="text-slate-400">
								{liveStatus === "closed"
									? "Stream unavailable — the server has no Redis connection, or the socket was refused."
									: liveStatus === "connecting"
										? "Connecting…"
										: "Waiting for events…"}
							</p>
						)}
						{live.map((e, i) => (
							<div
								key={`${e.sessionId}-${i}`}
								className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-1.5"
							>
								<span className="font-medium text-slate-700">
									{e.eventType}
								</span>
								<span className="truncate text-slate-500">
									{e.payload?.query ?? e.payload?.productId ?? ""}
									{e.payload?.objectPosition
										? ` @${e.payload.objectPosition}`
										: ""}
								</span>
							</div>
						))}
					</div>
				</Panel>
			</div>

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
				{/* Top queries */}
				<Panel title="Top search queries">
					<Table
						head={["Query", "Searches", "Avg results"]}
						rows={queries.map((q) => [
							q.query,
							String(q.searches),
							String(q.avgResults ?? "—"),
						])}
					/>
				</Panel>

				{/* Top products */}
				<Panel title="Top clicked products">
					<Table
						head={["Product", "Clicks", "Avg pos."]}
						rows={products.map((p) => [
							p.productName ?? `#${p.productId}`,
							String(p.clicks),
							String(p.avgPosition),
						])}
					/>
				</Panel>
			</div>

			{/* Zero-result searches */}
			<Panel title="Zero-result searches (content & recommendation opportunities)">
				<Table
					head={["Query", "Searches"]}
					rows={zeroResults.map((q) => [q.query, String(q.searches)])}
				/>
			</Panel>
		</div>
	);
};

const Table = ({ head, rows }: { head: string[]; rows: string[][] }) => (
	<div className="overflow-x-auto">
		<table className="w-full text-left text-[13px]">
			<thead>
				<tr className="border-b border-slate-200 text-slate-500">
					{head.map((h) => (
						<th key={h} className="pr-2 pb-2 font-medium">
							{h}
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{rows.length === 0 && (
					<tr>
						<td colSpan={head.length} className="py-3 text-slate-400">
							No data
						</td>
					</tr>
				)}
				{rows.map((r, i) => (
					<tr
						key={`${r[0]}-${i}`}
						className="border-b border-slate-100 last:border-0"
					>
						{r.map((cell, j) => (
							<td key={`${i}-${j}`} className="py-2 pr-2 text-slate-700">
								{cell}
							</td>
						))}
					</tr>
				))}
			</tbody>
		</table>
	</div>
);

export default Analytics;
